import pick from 'lodash/pick'
import omit from 'lodash/omit'
import set from 'lodash/set'
import pickBy from 'lodash/pickBy'
import SellingTypesAPIs from '@/API/ordering/selling-types-api'
import { REFUND_METHOD } from '@/constants/order-constants'

export default {
  namespaced: true,
  state: {
    fetchedSettings: {},
    tablesNumber: 3,
    floorsNumber: 1,
    isLoading: false,
    currentBranchId: null,
  },
  getters: {
    currentBranchId: (state: any, getters: any, rootState: any, rootGetters: any) => state.currentBranchId ?? rootGetters['branches/currentBranchId'],
    settings: (state: any, getters: any) => state.fetchedSettings[getters.currentBranchId] ?? {},
    sellingTypes: (state: any, getters: any, rootState: any, rootGetters: any) => omit(rootGetters['branches/currentBranch']?.selling_types ?? {}, ['product_service']),
    activeSellingTypes: (state: any, getters: any) => Object.keys(pickBy(getters.sellingTypes, Boolean)).map((type: any) => (type === 'dine_in_view' ? 'dine_in' : type)).filter(type => ['dine_in', 'car_pickup', 'delivery'].indexOf(type) > -1),
    menuTypes: (state: any, getters: any, rootState: any, rootGetters: any) => pick(rootGetters['branches/currentBranch'] ?? {}, ['level', 'mobile_orders', 'tablet_menu', 'qr_menu']),
    isQrMenuEnabled: (state: any, getters: any) => getters.menuTypes.qr_menu,
    isTabletMenuEnabled: (state: any, getters: any) => getters.menuTypes.tablet_menu,
    dineInSettings: (state: any, getters: any) => getters.settings?.dine_in_setting ?? {},
    deliverySettings: (state: any, getters: any) => getters.settings?.delivery_setting ?? {},
    carPickupSettings: (state: any, getters: any) => getters.settings?.car_pickup_setting ?? {},
    productServiceSettings: (state: any, getters: any) => getters.settings?.product_setting ?? {},
    tablesNumber: (state: any) => state.tablesNumber ?? {},
    floorsNumber: (state: any) => state.floorsNumber ?? {},
    paymentMethods: (state: any, getters: any) => ['cash', 'pay_with_card', 'credit', 'pay_later'].reduce((prev: any, method: string) => ({
      ...prev,
      [method]: {
        dine_in: !!getters.dineInSettings[method], delivery: !!getters.deliverySettings[method], car_pickup: !!getters.carPickupSettings[method],
      },
    }), {}),
    isAnySellingTypeEnableOnlineMethod: (state: any, getters: any) => Object.values(getters.paymentMethods.credit).some(Boolean),
    isAnySellingTypeEnabled: (state: any, getters: any) => Object.values(getters.sellingTypes).some(Boolean),
    isDineinViewOnlyEnabled: (state: any, getters: any) => getters.sellingTypes.dine_in_view && !Object.values(pick(getters.sellingTypes, ['dine_in', 'car_pickup', 'delivery'])).some(Boolean),
    vendorStatus: (state: any, getters: any, rootState: any, rootGetters: any) => rootGetters['integrations/activeVendorIntegration']?.status,
    vendorId: (state: any, getters: any, rootState: any, rootGetters: any) => rootGetters['integrations/activeVendorIntegration']?.id,
    vendorNote: (state: any, getters: any, rootState: any, rootGetters: any) => rootGetters['branches/currentBranch'].vendor_rejection_note,
    isOnlinePaymentEnabled: (state: any, getters: any, rootState: any, rootGetters: any) => [1, 2].includes(rootGetters['integrations/activeVendorIntegration']?.status),
    isLoading: (state: any) => state.isLoading ?? false,
    refundMethod: (state: any, getters: any) => Number(getters.settings?.refund_method ?? REFUND_METHOD.BOTH),
  },
  mutations: {
    SET_SETTINGS(state: any, { branchId, settings }: any) {
      state.fetchedSettings = {
        ...state.fetchedSettings,
        [branchId]: settings,
      }
    },
    SET_TABLES_NUMBER(state: any, value: number) {
      state.tablesNumber = value
    },
    SET_FLOORS_NUMBER(state: any, value: number) {
      state.floorsNumber = value
    },
    TOGGLE_LOADING(state: any, value: boolean) {
      state.isLoading = value
    },
    SET_CURRENT_BRANCH_ID(state: any, branchId: number) {
      state.currentBranchId = branchId
    },
    SET_PAYMENT_METHODS(state: any, { branchId, paymentMethods }: any) {
      ['dine_in_setting', 'delivery_setting', 'car_pickup_setting'].forEach(settings => {
        ['cash', 'pay_with_card', 'credit', 'pay_later'].forEach(method => {
          const sellingType = settings.replace('_setting', '')
          set(state.fetchedSettings, `${branchId}.${settings}.${method}`, paymentMethods[method][sellingType])
        })
      })
    },
    SET_REFUND_METHOD(state: any, { branchId, refundMethod }: any) {
      set(state.fetchedSettings, `${branchId}.refund_method`, refundMethod)
    },
  },
  actions: {
    async FETCH_SELLING_SETTINGS(context: any, selectedBranchId?: any) {
      const branchId = selectedBranchId ?? context.rootGetters['branches/currentBranchId']
      context.commit('SET_CURRENT_BRANCH_ID', branchId)
      const cached = context.state.fetchedSettings[branchId]
      if (cached?.dine_in_setting && Object.prototype.hasOwnProperty.call(cached, 'refund_method')) {
        return cached
      }
      context.commit('TOGGLE_LOADING', true)
      const raw = await SellingTypesAPIs.getSellingTypes(branchId)
      const settings = raw?.dine_in_setting ? raw : { ...(raw?.data ?? raw), refund_method: raw?.refund_method ?? raw?.data?.refund_method }
      context.commit('TOGGLE_LOADING', false)
      context.commit('SET_SETTINGS', { branchId, settings })
      return settings
    },
    async UPDATE_DINEIN_SETTINGS(context: any, { newSettings, selectedBranchId = null }: any) {
      const branchId = selectedBranchId ?? context.rootGetters['branches/currentBranchId']
      const { settings, dineInSettings } = context.getters
      const dine_in_setting = {
        ...dineInSettings,
        ...newSettings,
      }
      context.commit('SET_SETTINGS', { branchId, settings: { ...settings, dine_in_setting } })
    },
    async UPDATE_DELIVERY_SETTINGS(context: any, newSettings: any) {
      const branchId = newSettings?.selectedBranchId ?? context.rootGetters['branches/currentBranchId']
      const { settings, deliverySettings } = context.getters
      const delivery_setting = {
        ...deliverySettings,
        ...newSettings,
      }
      context.commit('SET_SETTINGS', { branchId, settings: { ...settings, delivery_setting } })
    },
    async UPDATE_PICKUP_SETTINGS(context: any, newSettings: any) {
      const branchId = newSettings?.selectedBranchId ?? context.rootGetters['branches/currentBranchId']
      const { settings, carPickupSettings } = context.getters
      const car_pickup_setting = {
        ...carPickupSettings,
        ...newSettings,
      }
      context.commit('SET_SETTINGS', { branchId, settings: { ...settings, car_pickup_setting } })
    },
  },
}
